import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Stethoscope, ShoppingBasket, HeartPulse, Milk } from "lucide-react";
import { useContent } from "@/lib/content";

const WORK_ICONS = [Stethoscope, ShoppingBasket, HeartPulse, Milk];

const Section = ({ overline, title, children, testid }) => (
  <section className="border-t border-border" data-testid={testid}>
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
      {overline && <div className="overline mb-3">{overline}</div>}
      <h2 className="font-display text-4xl lg:text-5xl mb-6">{title}</h2>
      {children}
    </div>
  </section>
);

export default function About() {
  const { c, img } = useContent();
  const heroImage = img("about.hero.image", "");

  return (
    <main data-testid="about-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-14 pb-12">
        <div className="overline mb-3" data-testid="about-overline">{c("about.hero.overline")}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" data-testid="about-hero-title">
          {c("about.hero.title")}
        </h1>
        <p className="text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="about-intro">
          {c("about.hero.intro")}
        </p>
        {heroImage && (
          <img
            src={heroImage}
            alt={c("about.hero.title")}
            className="mt-10 rounded-[2rem] w-full aspect-[16/7] object-cover border border-border"
            data-testid="about-hero-image"
          />
        )}
      </section>

      <Section overline="Where we are going" title={c("about.vision.title")} testid="about-vision">
        <p className="text-muted-foreground leading-relaxed whitespace-pre-line max-w-3xl" data-testid="about-vision-body">
          {c("about.vision.body")}
        </p>
      </Section>

      <Section overline="What we set out to do" title={c("about.mission.title")} testid="about-mission">
        <p className="text-muted-foreground leading-relaxed whitespace-pre-line max-w-3xl" data-testid="about-mission-body">
          {c("about.mission.body")}
        </p>
      </Section>

      <Section overline="In practice" title={c("about.work.title")} testid="about-work">
        <p className="text-muted-foreground leading-relaxed max-w-2xl mb-10">{c("about.work.body")}</p>
        <div className="grid sm:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((n, i) => {
            const Icon = WORK_ICONS[i];
            const key = ["item1", "item2", "item3", "item4"][i];
            return (
              <div
                key={key}
                className="border border-border rounded-2xl p-6 hover:border-primary/40 hover:bg-accent/40 transition-colors"
                data-testid={`about-work-${n}`}
              >
                <Icon className="text-secondary mb-4" size={26} />
                <h3 className="font-display text-2xl mb-2">{c(`about.work.${key}_title`)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {c(`about.work.${key}_body`)}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      <section className="border-t border-border bg-primary text-primary-foreground" data-testid="about-philosophy">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="leaf-divider max-w-xs mb-6"><Leaf size={14} /></div>
          <h2 className="font-display text-3xl lg:text-4xl italic mb-5">{c("about.philosophy.title")}</h2>
          <p className="opacity-80 leading-relaxed max-w-3xl whitespace-pre-line">{c("about.philosophy.body")}</p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/appointments" className="btn-pill bg-secondary text-secondary-foreground h-12 px-7 inline-flex items-center" data-testid="about-book-cta">
              Book a Consultation
            </Link>
            <Link to="/shop" className="btn-pill border border-primary-foreground/30 h-12 px-7 inline-flex items-center hover:bg-primary-foreground/10" data-testid="about-shop-cta">
              Browse the Shop
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
